/**
 * Patients created without a mobile number or email still need a login-style identity, so
 * the platform gives them an internal placeholder email. The structure is shared with the
 * server (in.aumhum.aumhum.service.health.patient.NoContactEmailPolicy):
 *
 *     no-contact.<12 lowercase alphanumerics>@<domain>
 *
 * The `no-contact.` prefix is what makes such a patient recognisable everywhere; the domain
 * comes from the public setup payload (`noContactEmailDomain`) and only falls back to the
 * default below when the server has not sent one.
 */

export const NO_CONTACT_EMAIL_PREFIX = 'no-contact.'
export const DEFAULT_NO_CONTACT_EMAIL_DOMAIN = 'aumhum.xyz'

let configuredDomain = DEFAULT_NO_CONTACT_EMAIL_DOMAIN

function cleanDomain(domain) {
  if (typeof domain !== 'string') return ''
  return domain.trim().replace(/^@+/, '').toLowerCase()
}

/** Called once the public setup payload is available; harmless to call repeatedly. */
export function applyNoContactEmailConfig(setupData) {
  const next = cleanDomain(setupData?.noContactEmailDomain)
  if (next) configuredDomain = next
  return configuredDomain
}

export function getNoContactEmailDomain(domainOverride) {
  return cleanDomain(domainOverride) || configuredDomain
}

function randomToken(length = 12) {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let out = ''
  const cryptoObj = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined
  if (cryptoObj?.getRandomValues) {
    const bytes = new Uint8Array(length)
    cryptoObj.getRandomValues(bytes)
    for (let i = 0; i < length; i += 1) out += alphabet[bytes[i] % alphabet.length]
    return out
  }
  for (let i = 0; i < length; i += 1) out += alphabet[Math.floor(Math.random() * alphabet.length)]
  return out
}

export function generateNoContactEmail(domainOverride) {
  return `${NO_CONTACT_EMAIL_PREFIX}${randomToken()}@${getNoContactEmailDomain(domainOverride)}`
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * True for the structured placeholder, and for the two legacy shapes the platform used to
 * generate (a bare 32-hex uuid, or `<name><12 alnum>`) on the same domain when the patient
 * record is flagged anonymous. Pass the patient when you have it so legacy records resolve.
 */
export function isNoContactEmail(email, { patient, domain } = {}) {
  if (typeof email !== 'string' || !email) return false
  const normalized = email.trim().toLowerCase()
  const domainPattern = escapeRegExp(getNoContactEmailDomain(domain))
  if (new RegExp(`^${escapeRegExp(NO_CONTACT_EMAIL_PREFIX)}[a-z0-9]{6,}@${domainPattern}$`).test(normalized)) {
    return true
  }
  if (new RegExp(`^[0-9a-f]{32}@${domainPattern}$`).test(normalized)) {
    return true
  }
  if (patient?.anonymous && new RegExp(`@${domainPattern}$`).test(normalized)) {
    return true
  }
  return false
}

/** A patient the clinic cannot reach: flagged anonymous, or only carrying a placeholder email. */
export function patientHasNoContactDetails(patient, options = {}) {
  if (!patient) return false
  if (patient.anonymous) return true
  const hasMobile = typeof patient.mobile === 'string' && patient.mobile.trim().length > 0
  if (hasMobile) return false
  return isNoContactEmail(patient.email, { ...options, patient })
}

/** Email safe to show in lists / headers: hides the internal placeholder. */
export function getDisplayableEmail(email, options = {}) {
  return isNoContactEmail(email, options) ? '' : email || ''
}
